import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Card,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  InputNumber,
  Rate,
  Button,
  Space,
  Spin,
  message,
  Modal,
  Divider,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import { UAE_EMIRATES, UAE_PAYMENT_MEANS, LEGAL_REGISTRATION_TYPES } from '@/constants/uaeEinvoice';
import {
  checkSupplierDuplicates,
  type SupplierDuplicateCandidate,
} from '@/services/supplier.service';
import { RelatedMocsPanel } from '@/components/moc/RelatedMocsPanel';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_SUPPLIER } from '@/constants/document-entity-types';

const { Title, Text } = Typography;
const { TextArea } = Input;

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

interface SupplierRow {
  id: string;
  supplier_code: string;
  supplier_name: string;
  supplier_type: string;
  category?: string;
  supplier_category_ids?: string[] | null;
  contact_person?: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  city?: string;
  country?: string;
  tax_registration_number?: string;
  emirate_code?: string;
  state?: string;
  electronic_address?: string;
  electronic_identifier?: string;
  legal_registration_number?: string;
  legal_registration_type?: string;
  payment_means_code?: string;
  payment_terms?: string;
  lead_time_days?: number;
  currency?: string;
  quality_rating?: number;
  delivery_rating?: number;
  service_rating?: number;
  is_active?: boolean;
}

interface SupplierCategoryRow {
  id: string;
  category_name: string;
  description?: string;
  is_active: boolean;
}

interface CountryRow {
  id: string;
  iso_code: string;
  name: string;
  sort_order?: number;
}

function listBasePath(pathname: string): string {
  return pathname.startsWith('/platform') ? '/platform/foundation/suppliers' : '/scm/procurement/suppliers';
}

function avgRating(s: SupplierRow): number {
  const q = Number(s.quality_rating) || 0;
  const d = Number(s.delivery_rating) || 0;
  const sv = Number(s.service_rating) || 0;
  if (q === 0 && d === 0 && sv === 0) return 0;
  return Math.round(((q + d + sv) / 3) * 2) / 2;
}

const SUPPLIERS_QUERY_KEY = ['procurement-suppliers'];
const SUPPLIERS_STATS_KEY = ['procurement-suppliers-statistics'];

const SupplierFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const listPath = listBasePath(location.pathname);
  const isEdit = Boolean(id);
  const [duplicateCandidates, setDuplicateCandidates] = useState<SupplierDuplicateCandidate[]>([]);
  const [dupChecking, setDupChecking] = useState(false);
  const [dupDismissed, setDupDismissed] = useState(false);
  const dupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dupSeqRef = useRef(0);

  const { data: supplier, isLoading } = useQuery({
    queryKey: ['procurement-supplier', id],
    queryFn: async () => {
      const res = await api.get(`/procurement/suppliers/${id}`);
      return unwrap<SupplierRow>(res);
    },
    enabled: isEdit && !!id,
  });

  const { data: supplierCategories = [] } = useQuery({
    queryKey: ['procurement-supplier-categories'],
    queryFn: async () => {
      const res = await api.get('/procurement/supplier-categories');
      const rows = unwrap<SupplierCategoryRow[]>(res);
      return Array.isArray(rows) ? rows : [];
    },
  });

  const { data: countries = [], isLoading: countriesLoading } = useQuery({
    queryKey: ['procurement-countries'],
    queryFn: async () => {
      const res = await api.get('/procurement/countries');
      const rows = unwrap<CountryRow[]>(res);
      return Array.isArray(rows) ? rows : [];
    },
  });

  const countryOptions = React.useMemo(() => {
    const rows = [...countries];
    const current = supplier?.country?.trim();
    if (current && !rows.some((c) => c.name === current)) {
      rows.push({ id: 'legacy', iso_code: '', name: current });
    }
    return rows;
  }, [countries, supplier?.country]);

  useEffect(() => {
    if (!isEdit || !supplier) return;
    const r = avgRating(supplier) || 3;
    const categoryIds: string[] = Array.isArray(supplier.supplier_category_ids)
      ? supplier.supplier_category_ids.filter(Boolean)
      : [];
    form.setFieldsValue({
      supplierName: supplier.supplier_name,
      supplierCode: supplier.supplier_code,
      contactPerson: supplier.contact_person,
      email: supplier.email,
      phone: supplier.phone,
      city: supplier.city,
      country: supplier.country || 'United Arab Emirates',
      address: supplier.address_line1,
      taxRegistration: supplier.tax_registration_number,
      emirateCode: supplier.emirate_code || supplier.state,
      electronicAddress: supplier.electronic_address,
      electronicIdentifier: supplier.electronic_identifier,
      legalRegistrationNumber: supplier.legal_registration_number,
      legalRegistrationType: supplier.legal_registration_type || 'TL',
      paymentMeansCode: supplier.payment_means_code || '30',
      supplierType: supplier.supplier_type,
      categoryIds,
      paymentTerms: supplier.payment_terms || 'Net 30',
      leadTime: supplier.lead_time_days ?? 7,
      currency: supplier.currency || 'AED',
      rating: r,
      status: supplier.is_active ? 'Active' : 'Inactive',
    });
  }, [isEdit, supplier?.id]);

  useEffect(() => {
    if (!isEdit || !supplier?.id || !supplier.category) return;
    const hasIds = Array.isArray(supplier.supplier_category_ids) && supplier.supplier_category_ids.length > 0;
    if (hasIds) return;
    if (!supplierCategories.length) return;
    const match = supplierCategories.find((c) => c.category_name === supplier.category);
    if (!match) return;
    const current = form.getFieldValue('categoryIds') as string[] | undefined;
    if (Array.isArray(current) && current.length > 0) return;
    form.setFieldsValue({ categoryIds: [match.id] });
  }, [isEdit, supplier?.id, supplier?.category, supplier?.supplier_category_ids, supplierCategories]);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        country: 'United Arab Emirates',
        supplierType: 'Vendor',
        paymentTerms: 'Net 30',
        leadTime: 7,
        currency: 'AED',
        rating: 3,
        status: 'Active',
      });
    }
  }, [isEdit, form]);

  useEffect(() => {
    if (isEdit) return;
    if (!supplierCategories.length) return;
    const current = form.getFieldValue('categoryIds') as string[] | undefined;
    if (!current?.length) {
      form.setFieldValue('categoryIds', [supplierCategories[0].id]);
    }
  }, [isEdit, supplierCategories, form]);

  const goBack = () => navigate(listPath);

  const runDuplicateCheck = useCallback(
    async (values?: {
      supplierName?: string;
      taxRegistration?: string;
      email?: string;
    }) => {
      const v = values ?? form.getFieldsValue();
      const seq = ++dupSeqRef.current;
      setDupChecking(true);
      try {
        const result = await checkSupplierDuplicates(
          {
            supplier_name: v.supplierName,
            tax_registration_number: v.taxRegistration,
            email: v.email,
          },
          isEdit ? id : undefined
        );
        if (seq !== dupSeqRef.current) return;
        setDuplicateCandidates(result.duplicates);
        if (result.has_duplicates) setDupDismissed(false);
      } catch {
        if (seq !== dupSeqRef.current) return;
        setDuplicateCandidates([]);
      } finally {
        if (seq === dupSeqRef.current) setDupChecking(false);
      }
    },
    [form, id, isEdit]
  );

  const scheduleDuplicateCheck = useCallback(
    (values?: { supplierName?: string; taxRegistration?: string; email?: string }) => {
      if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
      dupTimerRef.current = setTimeout(() => {
        void runDuplicateCheck(values);
      }, 400);
    },
    [runDuplicateCheck]
  );

  const onDuplicateFieldChange = () => {
    setDupDismissed(false);
    scheduleDuplicateCheck();
  };

  useEffect(
    () => () => {
      if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    },
    []
  );

  const saveSupplier = async (values: Record<string, unknown>) => {
    const rating = Number(values.rating) || 0;
    const payload: Record<string, unknown> = {
      supplier_name: values.supplierName,
      contact_person: values.contactPerson,
      email: values.email,
      phone: values.phone,
      city: values.city,
      country: values.country,
      address_line1: values.address,
      state: values.emirateCode,
      emirate_code: values.emirateCode,
      tax_registration_number: values.taxRegistration,
      electronic_address: values.electronicAddress,
      electronic_identifier: values.electronicIdentifier,
      legal_registration_number: values.legalRegistrationNumber,
      legal_registration_type: values.legalRegistrationType,
      payment_means_code: values.paymentMeansCode,
      tax_scheme_code: 'VAT',
      supplier_type: values.supplierType,
      supplier_category_ids: values.categoryIds,
      payment_terms: values.paymentTerms,
      lead_time_days: values.leadTime,
      currency: values.currency,
      quality_rating: rating,
      delivery_rating: rating,
      service_rating: rating,
      is_active: values.status === 'Active',
    };
    if (values.supplierCode && String(values.supplierCode).trim()) {
      payload.supplier_code = String(values.supplierCode).trim();
    }
    if (isEdit && id) {
      await api.put(`/procurement/suppliers/${id}`, payload);
      message.success('Supplier updated');
    } else {
      await api.post('/procurement/suppliers', payload);
      message.success('Supplier created');
    }
    await queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: SUPPLIERS_STATS_KEY });
    navigate(listPath);
  };

  const confirmDuplicatesAndSave = (values: Record<string, unknown>, duplicates: SupplierDuplicateCandidate[]) =>
    new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: 'Possible duplicate supplier',
        width: 520,
        content: (
          <div>
            <p>The following existing supplier(s) may match this record:</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {duplicates.map((d) => (
                <li key={d.id}>
                  <strong>{d.supplier_code}</strong> — {d.supplier_name} ({d.match_reasons.join(', ')})
                </li>
              ))}
            </ul>
          </div>
        ),
        okText: isEdit ? 'Save anyway' : 'Create anyway',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const dupResult = await checkSupplierDuplicates(
        {
          supplier_name: values.supplierName,
          tax_registration_number: values.taxRegistration,
          email: values.email,
        },
        isEdit ? id : undefined
      );
      setDuplicateCandidates(dupResult.duplicates);

      if (dupResult.has_duplicates && !dupDismissed) {
        const proceed = await confirmDuplicatesAndSave(values, dupResult.duplicates);
        if (!proceed) return;
      }

      await saveSupplier(values);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || error?.message || 'Save failed');
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
          Back
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {isEdit ? 'Edit supplier' : 'New supplier'}
        </Title>
      </Space>

      <Card>
        <Spin spinning={isEdit && isLoading}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            {duplicateCandidates.length > 0 && !dupDismissed ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Possible duplicate supplier"
                description={
                  <div>
                    {duplicateCandidates.map((d) => (
                      <div key={d.id} style={{ marginBottom: 4 }}>
                        <strong>{d.supplier_code}</strong> — {d.supplier_name} ({d.match_reasons.join(', ')}){' '}
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto' }}
                          onClick={() => navigate(`${listPath}/${d.id}/edit`)}
                        >
                          Open
                        </Button>
                      </div>
                    ))}
                  </div>
                }
                closable
                onClose={() => setDupDismissed(true)}
              />
            ) : null}
            {dupChecking ? (
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Checking for duplicates…
              </Text>
            ) : null}

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="supplierName"
                  label="Supplier name"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input
                    placeholder="Supplier legal name"
                    onChange={onDuplicateFieldChange}
                    onBlur={() => scheduleDuplicateCheck()}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="supplierCode"
                  label="Supplier code (optional)"
                  extra="Leave blank to auto-generate (SUP-001…). Must be unique if you enter one."
                >
                  <Input placeholder="Auto-generated if empty" disabled={isEdit} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="contactPerson" label="Contact person" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ required: true }, { type: 'email', message: 'Invalid email' }]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    onChange={onDuplicateFieldChange}
                    onBlur={() => scheduleDuplicateCheck()}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
                  <Input prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="city" label="City" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="country" label="Country" rules={[{ required: true }]} initialValue="United Arab Emirates">
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={countriesLoading}
                    options={countryOptions.map((c) => ({ value: c.name, label: c.name }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="taxRegistration" label="Tax registration (TRN)">
                  <Input onChange={onDuplicateFieldChange} onBlur={() => scheduleDuplicateCheck()} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="address" label="Address" rules={[{ required: true, message: 'Required for UAE e-invoice' }]}>
              <TextArea rows={2} />
            </Form.Item>

            <Divider orientation="left">UAE E-Invoice (seller)</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="emirateCode" label="Emirate" rules={[{ required: true, message: 'Required for UAE e-invoice' }]}>
                  <Select showSearch optionFilterProp="label" options={UAE_EMIRATES} placeholder="Select emirate" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="paymentMeansCode" label="Payment means code">
                  <Select options={UAE_PAYMENT_MEANS} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="electronicAddress" label="Electronic address (Peppol / email)">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="electronicIdentifier" label="Electronic identifier">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="legalRegistrationNumber" label="Legal registration #">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="legalRegistrationType" label="Legal registration type">
                  <Select options={LEGAL_REGISTRATION_TYPES} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="supplierType" label="Supplier type" rules={[{ required: true }]}>
                  <Select showSearch optionFilterProp="children">
                    <Select.Option value="Vendor">Vendor</Select.Option>
                    <Select.Option value="Material">Material</Select.Option>
                    <Select.Option value="Service">Service</Select.Option>
                    <Select.Option value="Subcontractor">Subcontractor</Select.Option>
                    <Select.Option value="Equipment">Equipment</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="categoryIds"
                  label="Supplier categories"
                  rules={[
                    { required: true, message: 'Select at least one category' },
                    { type: 'array', min: 1, message: 'Select at least one category' },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select categories"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={supplierCategories.map((c) => ({
                      value: c.id,
                      label: c.category_name,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="paymentTerms" label="Payment terms" rules={[{ required: true }]}>
                  <Select showSearch optionFilterProp="children">
                    <Select.Option value="COD">COD</Select.Option>
                    <Select.Option value="Net 15">Net 15</Select.Option>
                    <Select.Option value="Net 30">Net 30</Select.Option>
                    <Select.Option value="Net 45">Net 45</Select.Option>
                    <Select.Option value="Net 60">Net 60</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12} />
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="leadTime" label="Lead time (days)" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="currency" label="Currency" initialValue="AED">
                  <Select showSearch optionFilterProp="children">
                    <Select.Option value="AED">AED</Select.Option>
                    <Select.Option value="USD">USD</Select.Option>
                    <Select.Option value="EUR">EUR</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="rating" label="Rating" initialValue={3}>
                  <Rate />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="status" label="Status" initialValue="Active">
              <Select showSearch optionFilterProp="children">
                <Select.Option value="Active">Active</Select.Option>
                <Select.Option value="Inactive">Inactive</Select.Option>
              </Select>
            </Form.Item>

            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                Save
              </Button>
              <Button onClick={goBack}>Cancel</Button>
            </Space>
          </Form>
        </Spin>
      </Card>
      {isEdit && id ? (
        <>
          <ProcurementAttachmentsCard
            entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER}
            entityId={id}
            queryKeyPrefix={['documents', 'supplier']}
          />
          <RelatedMocsPanel supplierId={id} />
        </>
      ) : null}
    </div>
  );
};

export default SupplierFormPage;
